import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { useContext } from "react";

import { FileUpload, TextOutput } from "@com.mgmtp.a12.widgets/widgets-core";
import { StyledFileUpload } from "@com.mgmtp.a12.widgets/widgets-core/lib/file-upload/main/file-upload.styled";
import { addPrefix } from "@com.mgmtp.a12.widgets/widgets-core/lib/common";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon/main/icon.view";
import { Button } from "@com.mgmtp.a12.widgets/widgets-core/lib/button/main/button.view";
import { HintTooltip } from "@com.mgmtp.a12.widgets/widgets-core/lib/tooltip";
import {
	DefaultFormModelMap,
	DefaultWidgetMap,
	FormModelMap,
	WidgetMap
} from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import { FormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import { EnablementByButtonName } from "@com.mgmtp.a12.formengine/formengine-core/lib/view/internal/configuration/engine-configuration";
import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import IExternalEnumerationProvider, {
	EmptyExternalEnumerationProvider
} from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/services/external-enumeration-provider";
import { FormEngineViews } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";

import { RESOURCE_KEYS } from "../../../localization";
import { CUSTOM_SCREEN, CUSTOM_TEXT } from "../../../utilities/variables/modulfRuntimeAnnotations";
import { ActivityIdContext } from "../../../component/ActivityContext";
import { CustomProgressIndicator } from "../../../component/CustomProgressIndicator";

import { generateArchivalContent } from "../actions";
import { ArchiveModalDocument, isArchiveModalDocumentData } from "../archiveModalTypes";
import { ArchiveModuleFormDescriptor } from "../descriptors";

export const ArchiveFormEngine: React.ComponentType<FormEngineViews.FormEngineProps> = function ArchiveFormEngine(
	props
) {
	const data = useSelector(ActivitySelectors.data(props.activityId));
	const activity = useSelector(ActivitySelectors.activityById(props.activityId));
	const typedData = isArchiveModalDocumentData(data) ? data.document : undefined;
	const archiveSystem = ArchiveModuleFormDescriptor.isInstance(activity?.descriptor)
		? activity.descriptor.archiveSystem
		: undefined;
	const hasPreview =
		!!typedData?.root.metadata?.archivePreviewUrl ||
		(archiveSystem === "xakte" && !!typedData?.root.metadata?.jobUuid);
	const externalEnumerationProvider = React.useMemo(
		() => (typedData ? getProcessFormEngineEnumProvider(typedData) : EmptyExternalEnumerationProvider),
		[typedData]
	);
	const buttonEnablements = React.useMemo(() => {
		return {
			byButtonName: {
				Verakten: {
					hidden: !(hasPreview && typedData?.root.metadata?.status !== "error")
				},
				abbrechen: {
					disabled: typedData?.root.metadata?.cancelDisabled
				}
			} satisfies EnablementByButtonName
		};
	}, [hasPreview, typedData?.root.metadata?.cancelDisabled, typedData?.root.metadata?.status]);
	const isBusy = typedData?.root.metadata?.busy;
	return (
		<InitialAttachmentsForcedContext value={typedData?.root.metadata?.forceInitialAttachments ?? false}>
			<ActivityIdContext value={props.activityId}>
				<CustomProgressIndicator hide={!isBusy}>
					<FormEngineViews.FormEngine
						{...props}
						formModelMap={customFormModelMap}
						widgetMap={customWidgetMap}
						externalEnumerationProvider={externalEnumerationProvider}
						enablements={buttonEnablements}
					/>
				</CustomProgressIndicator>
			</ActivityIdContext>
		</InitialAttachmentsForcedContext>
	);
};

const INITIAL_ATTACHMENTS_ELEMENT_REF = "group_0ba1c";
const InitialAttachmentsForcedContext = React.createContext(false);

function getProcessFormEngineEnumProvider(data: ArchiveModalDocument): IExternalEnumerationProvider {
	return source =>
		data.root.externalEnumCache
			?.find(it => it.key === source)
			?.options?.reduce((prev, cur) => ({ ...prev, [cur.id]: { de: cur.value } }), {}) ?? {};
}

const CutsomTextCell: React.ComponentType<FormModelMap.FormModelComponentProps<FormModel.CustomCell>> =
	function CustomTextCell(props) {
		const activityId = useContext(ActivityIdContext) ?? "";
		const activity = useSelector(ActivitySelectors.activityById(activityId));
		const archiveSystem = ArchiveModuleFormDescriptor.isInstance(activity?.descriptor)
			? activity.descriptor.archiveSystem
			: "eldorado";
		if (
			props.modelElement.annotations?.find(it => it.name === CUSTOM_TEXT.name)?.value ===
			CUSTOM_TEXT.values.VERAKTUNGSHINWEISE
		) {
			return (
				<div>
					<p>
						Die ausgewählten Dokumente des Veraktungsinhalts werden an{" "}
						{archiveSystem === "eldorado" ? "ELDORADO" : "xAkte"} zur Veraktung übermittelt. Standardmäßig
						umfasst die Veraktung zwei Dokumente mit folgendem Inhalt:
					</p>
					<ul>
						<li>
							Antragsdaten: aktueller Antrag, initialer Antrag, initial angehängte Dokumente sowie eine
							Änderungshistorie
						</li>
						<li>Prozessdaten: aktuelle Prozessdaten und eine Änderungshistorie</li>
					</ul>
					<p>
						Optional können zusätzlich hochgeladene und generierte Dokumente für die Veraktung ausgewählt
						werden.
					</p>
				</div>
			);
		}
		return <DefaultFormModelMap.CustomCell.component {...props} />;
	};

const ExcludedAttachmentsWarning: React.FC<{ activityId: string }> = function ExcludedAttachmentsWarning({
	activityId
}) {
	const { localizer } = useContext(LocalizerContext);
	const data = useSelector(ActivitySelectors.data(activityId));
	const metadata = isArchiveModalDocumentData(data) ? data.document.root.metadata : undefined;
	const excludedAttachments = metadata?.excludedAttachments;
	const excludedMsgAttachments = metadata?.excludedMsgAttachments;
	const duplicatedItemTexts = metadata?.duplicatedItemTexts;

	if (!excludedAttachments && !excludedMsgAttachments && !duplicatedItemTexts) {
		return null;
	}

	return (
		<>
			{excludedAttachments && (
				<ExcludedAttachmentsWarningBox role="alert">
					{(
						localizer({
							key: RESOURCE_KEYS.features.notification.warning.eldorado.excludedAttachments
						}) ?? ""
					).replace("$excludedFiles$", excludedAttachments)}
				</ExcludedAttachmentsWarningBox>
			)}
			{excludedMsgAttachments && (
				<ExcludedAttachmentsWarningBox role="alert">
					{(
						localizer({
							key: RESOURCE_KEYS.features.notification.warning.eldorado.excludedMsgAttachments
						}) ?? ""
					).replace("$excludedFiles$", excludedMsgAttachments)}
				</ExcludedAttachmentsWarningBox>
			)}
			{duplicatedItemTexts && (
				<ExcludedAttachmentsWarningBox role="alert">
					{(
						localizer({
							key: RESOURCE_KEYS.features.notification.warning.eldorado.duplicatedItemTexts
						}) ?? ""
					).replace("$duplicatedText$", duplicatedItemTexts)}
				</ExcludedAttachmentsWarningBox>
			)}
		</>
	);
};

const CustomScreenElement: React.ComponentType<FormModelMap.FormModelComponentProps<FormModel.CustomScreenElement>> =
	function CustomScreenElement(props) {
		const dispatch = useDispatch();
		const activityId = useContext(ActivityIdContext) ?? "";
		const data = useSelector(ActivitySelectors.data(activityId ?? ""));
		const activity = useSelector(ActivitySelectors.activityById(activityId ?? ""));
		const { localizer } = useContext(LocalizerContext);
		const generateArchiveContent = React.useCallback(() => {
			if (!activity) {
				return;
			}
			dispatch(generateArchivalContent({ activityId }));
		}, [activity, activityId, dispatch]);

		const annotationValue = props.modelElement.annotations?.find(it => it.name === CUSTOM_SCREEN.name)?.value;

		if (annotationValue === CUSTOM_SCREEN.values.EXCLUDED_ATTACHMENTS_WARNING) {
			return <ExcludedAttachmentsWarning activityId={activityId} />;
		}

		if (annotationValue === CUSTOM_SCREEN.values.ARCHIVE_WIDGET) {
			const rootData = isArchiveModalDocumentData(data) ? data.document.root : undefined;
			const archiveSystem = ArchiveModuleFormDescriptor.isInstance(activity?.descriptor)
				? activity.descriptor.archiveSystem
				: "eldorado";
			const hasPreview =
				!!rootData?.metadata?.archivePreviewUrl || (archiveSystem === "xakte" && !!rootData?.metadata?.jobUuid);
			return (
				<ArchiveWidget
					title={""}
					actionItem={
						<HintTooltip
							text={
								archiveSystem === "eldorado"
									? "Der hier generierte Veraktungsinhalt kann vor der Veraktung als ZIP-Archiv heruntergeladen werden, um die ausgewählten Dokumente zu prüfen. Nach der Veraktung steht dieses ZIP-Archiv am Vorgang zum Herunterladen zur Verfügung."
									: "Der Veraktungsinhalt wird als xDOMEA-Paket vorbereitet und nach Bestätigung an die konfigurierte xAkte übermittelt."
							}
						></HintTooltip>
					}
					label={"Veraktungsinhalt*"}
					onUploadAreaClick={hasPreview ? undefined : generateArchiveContent}
					readOnly={true}
				>
					{!hasPreview && (
						<DummyButton onClick={generateArchiveContent}>
							{localizer({
								key: RESOURCE_KEYS.features.modals.eldorado.generateButton
							})}
						</DummyButton>
					)}

					{rootData?.metadata?.archivePreviewUrl && (
						<TextOutput>
							<StyledFileUpload.StyledFieldUploadFileNameLink
								title={localizer({ key: RESOURCE_KEYS.features.modals.eldorado.previewTitle })}
								href={rootData.metadata?.archivePreviewUrl}
								linkAttributes={{
									download: `${archiveSystem === "xakte" ? "xAkte" : "FFH"}_${rootData?.betreff}-Vorschau.zip`
								}}
								className={addPrefix("-u-truncate")}
							>
								<Icon iconTheme={"outlined"}>archive</Icon>
								{localizer({
									key: RESOURCE_KEYS.features.modals.eldorado.previewDownloadLinkTitle
								})}
							</StyledFileUpload.StyledFieldUploadFileNameLink>
						</TextOutput>
					)}
					{archiveSystem === "xakte" &&
						rootData?.metadata?.jobUuid &&
						!rootData.metadata.archivePreviewUrl && (
							<TextOutput>
								<PreviewFileName>
									<Icon iconTheme={"outlined"}>archive</Icon>
									{rootData.metadata.previewFileName ?? "xDOMEA-Paket"}
								</PreviewFileName>
							</TextOutput>
						)}
				</ArchiveWidget>
			);
		}
		return <DefaultFormModelMap.CustomScreenElement.component {...props} />;
	};

const ArchiveWidget = styled(FileUpload)`
	[data-role="file-upload-content-inner"] {
		pointer-events: auto;
	}
`;

const DummyButton = styled(Button)`
	pointer-events: none;
`;

const PreviewFileName = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
`;

const ExcludedAttachmentsWarningBox = styled.div`
	background-color: #fff3cd;
	border: 1px solid #ffc107;
	border-radius: 4px;
	color: #856404;
	padding: 12px 16px;
	margin-bottom: 16px;
`;

const customFormModelMap: FormModelMap = {
	...DefaultFormModelMap,
	Control: {
		component: function ArchiveControl(props) {
			if (props.modelElement.elementRef !== INITIAL_ATTACHMENTS_ELEMENT_REF) {
				return <DefaultFormModelMap.Control.component {...props} />;
			}
			return (
				<InitialAttachmentsControlContext value={true}>
					<DefaultFormModelMap.Control.component {...props} />
				</InitialAttachmentsControlContext>
			);
		}
	},
	CustomCell: {
		component: CutsomTextCell
	},
	CustomScreenElement: {
		component: CustomScreenElement
	}
};

const InitialAttachmentsControlContext = React.createContext(false);

const customWidgetMap: WidgetMap = {
	...DefaultWidgetMap,
	MultiSelect: function ArchiveMultiSelect(props) {
		const isInitialAttachmentsControl = React.useContext(InitialAttachmentsControlContext);
		const forceInitialAttachments = React.useContext(InitialAttachmentsForcedContext);
		return (
			<DefaultWidgetMap.MultiSelect
				{...props}
				readonly={props.readonly || (isInitialAttachmentsControl && forceInitialAttachments)}
			/>
		);
	}
};
